import apiClient from "./client";
import type { PaginatedResponse, TrendingKeyword } from "@/types/common";
import type {
  ThesisListItem,
  ThesisResponse,
  ThesisSearchParams,
  CreateThesisPayload,
  UpdateThesisPayload,
  UpdateThesisStatusPayload,
  ReadingSessionResponse,
} from "@/types/thesis";

// ─── Search / List ───────────────────────────────────────────────────────────

/**
 * List / search theses with pagination and filters.
 *
 * Maps to `GET /api/theses?q=...&department_id=...&page=1&per_page=20`
 */
export async function getTheses(
  params: ThesisSearchParams = {},
): Promise<PaginatedResponse<ThesisListItem>> {
  // Build a clean params object — strip undefined values so axios
  // doesn't send `?q=undefined` etc.
  const query: Record<string, string | number> = {};

  if (params.q) query.q = params.q;
  if (params.department_id) query.department_id = params.department_id;
  if (params.degree_level) {
    query.degree_level = Array.isArray(params.degree_level)
      ? params.degree_level.join(",")
      : params.degree_level;
  }
  if (params.thesis_type) query.thesis_type = params.thesis_type;
  if (params.thesis_language) query.thesis_language = params.thesis_language;
  if (params.year_from != null) query.year_from = params.year_from;
  if (params.year_to != null) query.year_to = params.year_to;
  if (params.status) query.status = params.status;
  if (params.sort_by) query.sort_by = params.sort_by;
  if (params.sort_order) query.sort_order = params.sort_order;
  if (params.page) query.page = params.page;
  if (params.per_page) query.per_page = params.per_page;

  const { data } = await apiClient.get<PaginatedResponse<ThesisListItem>>(
    "/theses",
    { params: query },
  );
  return data;
}

// ─── Detail ──────────────────────────────────────────────────────────────────

/**
 * Get full thesis detail by ID.
 *
 * Maps to `GET /api/theses/:id`
 */
export async function getThesis(id: string): Promise<ThesisResponse> {
  const { data } = await apiClient.get<ThesisResponse>(`/theses/${id}`);
  return data;
}

// ─── Related Theses ──────────────────────────────────────────────────────────

/**
 * Get related theses for a given thesis.
 *
 * Maps to `GET /api/theses/:id/related`
 */
export async function getRelatedTheses(id: string): Promise<ThesisListItem[]> {
  const { data } = await apiClient.get<ThesisListItem[]>(
    `/theses/${id}/related`,
  );
  return data;
}

// ─── CRUD (Admin) ────────────────────────────────────────────────────────────

/**
 * Create a new thesis record (admin only).
 *
 * Maps to `POST /api/theses`
 */
export async function createThesis(
  payload: CreateThesisPayload,
): Promise<ThesisResponse> {
  const { data } = await apiClient.post<ThesisResponse>("/theses", payload);
  return data;
}

/**
 * Update an existing thesis (admin only).
 *
 * Maps to `PUT /api/theses/:id`
 */
export async function updateThesis(
  id: string,
  payload: UpdateThesisPayload,
): Promise<ThesisResponse> {
  const { data } = await apiClient.put<ThesisResponse>(
    `/theses/${id}`,
    payload,
  );
  return data;
}

/**
 * Soft-delete / archive a thesis (admin only).
 *
 * Maps to `DELETE /api/theses/:id`
 */
export async function deleteThesis(id: string): Promise<void> {
  await apiClient.delete(`/theses/${id}`);
}

/**
 * Update thesis status — approve / reject / archive (super admin only).
 *
 * Maps to `PUT /api/theses/:id/status`
 */
export async function updateThesisStatus(
  id: string,
  payload: UpdateThesisStatusPayload,
): Promise<ThesisResponse> {
  const { data } = await apiClient.put<ThesisResponse>(
    `/theses/${id}/status`,
    payload,
  );
  return data;
}

// ─── Reading Session ─────────────────────────────────────────────────────────

/**
 * Create a reading session for a thesis.
 * Returns a short-lived session token used to fetch individual pages.
 *
 * Maps to `GET /api/theses/:id/session`
 */
export async function createReadingSession(
  thesisId: string,
): Promise<ReadingSessionResponse> {
  const { data } = await apiClient.get<ReadingSessionResponse>(
    `/theses/${thesisId}/session`,
  );
  return data;
}

/**
 * Fetch a single rendered page image for the secure reader.
 * Returns a Blob (image/png).
 *
 * Maps to `GET /api/theses/:id/read/:page`
 */
export async function getThesisPage(
  thesisId: string,
  page: number,
  sessionToken: string,
): Promise<Blob> {
  const { data } = await apiClient.get(`/theses/${thesisId}/read/${page}`, {
    params: { session_token: sessionToken },
    responseType: "blob",
    headers: {
      "X-Reading-Session": sessionToken,
    },
  });
  return data;
}

// ─── Upload ──────────────────────────────────────────────────────────────────

/**
 * Upload a PDF file for a thesis (admin only).
 *
 * Maps to `POST /api/theses/:id/upload`
 */
export async function uploadThesisFile(
  thesisId: string,
  file: File,
  onProgress?: (percentage: number) => void,
): Promise<ThesisResponse> {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await apiClient.post<ThesisResponse>(
    `/theses/${thesisId}/upload`,
    formData,
    {
      headers: { "Content-Type": undefined },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const pct = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(pct);
        }
      },
    },
  );
  return data;
}

/**
 * Upload a custom cover image for a thesis (admin only).
 *
 * Maps to `POST /api/theses/:id/cover`
 */
export async function uploadThesisCover(
  thesisId: string,
  file: File,
  onProgress?: (percentage: number) => void,
): Promise<ThesisResponse> {
  const formData = new FormData();
  formData.append("cover", file);

  const { data } = await apiClient.post<ThesisResponse>(
    `/theses/${thesisId}/cover`,
    formData,
    {
      headers: { "Content-Type": undefined },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const pct = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          onProgress(pct);
        }
      },
    },
  );
  return data;
}

// ─── Discovery / Stats ───────────────────────────────────────────────────────

/**
 * Get recently added theses (approved, sorted newest first).
 */
export async function getRecentTheses(
  limit: number = 8,
): Promise<ThesisListItem[]> {
  const res = await getTheses({
    sort_by: "date",
    sort_order: "desc",
    status: "approved",
    per_page: limit,
    page: 1,
  });
  return res.data;
}

/**
 * Get most popular theses (approved, sorted by views).
 */
export async function getPopularTheses(
  limit: number = 4,
): Promise<ThesisListItem[]> {
  const res = await getTheses({
    sort_by: "views",
    sort_order: "desc",
    status: "approved",
    per_page: limit,
    page: 1,
  });
  return res.data;
}

/**
 * Get top-rated theses (approved, sorted by rating).
 */
export async function getTopRatedTheses(
  limit: number = 6,
): Promise<ThesisListItem[]> {
  const res = await getTheses({
    sort_by: "rating",
    sort_order: "desc",
    status: "approved",
    per_page: limit,
    page: 1,
  });
  return res.data;
}

/**
 * Get trending keywords.
 *
 * Maps to `GET /api/stats/trending-keywords`
 */
export async function getTrendingKeywords(
  limit: number = 20,
): Promise<TrendingKeyword[]> {
  const { data } = await apiClient.get<TrendingKeyword[]>(
    "/stats/trending-keywords",
    { params: { limit } },
  );
  return data;
}
